import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Loader2, Lock, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useClassifyClient } from "@/hooks/useClients";
import {
  RECORD_TYPE_LABEL, BUSINESS_MODEL_LABEL, ADDITIONAL_ACTIVITY_LABEL, LEGAL_IDENTITY_LABEL,
} from "@/lib/client360Labels";
import type { ClientRecordType, ClientBusinessModel, Client360Header } from "@/api/clients";

/**
 * Client360 classification drawer — v2.1 (Classification Grain Reconcile Hotfix P0).
 *
 * ★ GRAIN SPLIT (§1/§4): this drawer edits the HOLDING-CLASSIFICATION only and the
 * save-flow persists EXACTLY three holding-grain axes:
 *   • recordType            → CClientHolding.cCRecordType
 *   • primaryBusinessModel  → linked CClientBusinessProfile (REUSE)
 *   • additionalActivities  → CClientHolding.cCAdditionalActivities
 *
 * Legal identity (тип особи / правова форма / ЄДРПОУ / ІПН) is READ-ONLY here
 * (§4b VP2) — shown with a link to the «Юрособи» surface where it is edited
 * (Этап4). A holding (e.g. Master Zoo · 3 ТОВ) has no single legal form, so the
 * drawer never writes it and never trips the LE legal-form validation (#10).
 */

// record types that carry a business model + a legal contour (b2b/breeder/retail).
const RECORD_TYPES: ClientRecordType[] = [
  "real_b2b_client", "breeder_person", "technical_account",
  "event_exhibition_account", "internal_account", "retail_b2c_customer", "unknown",
];

// retail is reserved/disabled in the manager dropdown until a retail source is wired (§2 MP2 / VP4).
const RESERVED_RECORD_TYPES = new Set<ClientRecordType>(["retail_b2c_customer"]);

const BUSINESS_MODELS: ClientBusinessModel[] = [
  "independent_pet_shop", "retail_chain", "vet_clinic", "grooming_salon",
  "breeder_professional", "online_store", "marketplace_seller", "wholesale_reseller",
  "dealer_partner", "purchasing_group", "shelter_ngo", "trainer_kennel_club",
  "farm_agro_pet_point", "other",
];

const ADDITIONAL_ACTIVITIES = [
  "grooming", "vet_services", "online_sales", "wholesale", "retail_points",
  "breeding", "marketplace_sales", "training_events", "other",
];

/** record_type → which legal-identity section + whether a business model applies. */
type LegalMode = "b2b" | "breeder" | "unknown" | "none";
function legalModeFor(rt: ClientRecordType): LegalMode {
  if (rt === "real_b2b_client") return "b2b";
  if (rt === "breeder_person") return "breeder";
  if (rt === "unknown") return "unknown";
  return "none"; // technical / event / internal / retail
}
function showsBusinessModel(rt: ClientRecordType): boolean {
  return rt === "real_b2b_client" || rt === "breeder_person" || rt === "retail_b2c_customer";
}

interface Props {
  open: boolean;
  holdingId: string;
  header: Client360Header;
  /** Current primary business model (from the envelope BP summary) — prefill. */
  businessModel?: string | null;
  onClose: () => void;
}

export function ClassificationDrawer({ open, holdingId, header, businessModel, onClose }: Props) {
  const headingId = useId();
  const panelRef = useDrawerA11y(open, onClose);
  const navigate = useNavigate();
  const mutation = useClassifyClient(holdingId);

  const [recordType, setRecordType] = useState<ClientRecordType>(header.recordType ?? "unknown");
  const [model, setModel] = useState<string>(businessModel ?? "");
  const [activities, setActivities] = useState<string[]>(header.additionalActivities ?? []);
  const [moreOpen, setMoreOpen] = useState<boolean>((header.additionalActivities ?? []).length > 0);

  if (!open) return null;

  const legalMode = legalModeFor(recordType);
  const showBm = showsBusinessModel(recordType);
  const canSubmit = !mutation.isPending && recordType !== "unknown";

  const goEntities = () => { onClose(); navigate(clientRoutes.entities(holdingId)); };

  const toggleActivity = (a: string) =>
    setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const submit = () => {
    const payload: Parameters<typeof mutation.mutate>[0] = { recordType };
    if (showBm && model) payload.businessModel = model as ClientBusinessModel;
    payload.additionalActivities = activities;
    mutation.mutate(payload, { onSuccess: () => onClose() });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-y-auto bg-background shadow-2xl outline-none sm:w-[460px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <div className="min-w-0">
            <h3 id={headingId} className="text-base font-semibold">Класифікація клієнта</h3>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{header.displayName || header.name}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 px-5 py-5">
          <p className="rounded-lg border bg-muted/40 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
            Класифікація налаштовує <b className="font-semibold text-foreground">тип картки</b>: службові
            записи без зайвих попереджень, заводчики — без юр./точкових вимог.{" "}
            <b className="font-semibold text-foreground">Юридична ідентичність</b> (форма, ЄДРПОУ/ІПН)
            живе на рівні <b className="font-semibold text-foreground">юрособи</b> (з 1С) — не змішується з класифікацією.
          </p>

          {/* MP5 — branch-change warning: required-set may change; data kept/audited. */}
          <p className="rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-[11.5px] leading-relaxed text-warning-foreground">
            ⚠ Зміна типу/моделі може змінити <b>набір обов'язкових полів профілю</b>. Попередні
            дані профілю зберігаються (audited) і не видаляються мовчки.
          </p>

          {/* ─────────── SECTION 1: CLASSIFICATION (holding-grain) ─────────── */}
          <section className="rounded-xl border p-3.5">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-primary">Класифікація</span>
              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">рівень клієнта</span>
            </div>

            <div className="space-y-1.5">
              <label htmlFor={`${headingId}-rt`} className="block text-xs font-semibold text-muted-foreground">
                Тип запису
              </label>
              <select
                id={`${headingId}-rt`}
                value={recordType}
                onChange={(e) => setRecordType(e.target.value as ClientRecordType)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {RECORD_TYPES.map((rt) => {
                  const reserved = RESERVED_RECORD_TYPES.has(rt) && rt !== header.recordType;
                  return (
                    <option key={rt} value={rt} disabled={reserved}>
                      {RECORD_TYPE_LABEL[rt]}{reserved ? " (зарезервовано · не активний)" : ""}
                    </option>
                  );
                })}
              </select>

              {/* relationship direction — read-only, з 1С (reserved P1) */}
              {recordType === "real_b2b_client" && (
                <span className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-dashed bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
                  <ArrowLeftRight className="h-3 w-3" /> Напрямок: <b className="font-semibold">Покупець</b>
                  <span className="rounded bg-muted px-1 py-px text-[10px]">з 1С</span>
                </span>
              )}
            </div>

            {showBm && (
              <div className="mt-3 space-y-1.5">
                <label htmlFor={`${headingId}-bm`} className="block text-xs font-semibold text-muted-foreground">
                  Основна бізнес-модель <span className="font-normal text-muted-foreground/70">(драйвить гілку профілю)</span>
                </label>
                <select
                  id={`${headingId}-bm`}
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">— не вказано —</option>
                  {BUSINESS_MODELS.map((bm) => (
                    <option key={bm} value={bm}>{BUSINESS_MODEL_LABEL[bm]}</option>
                  ))}
                </select>

                {!moreOpen ? (
                  <button
                    type="button"
                    onClick={() => setMoreOpen(true)}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    ＋ Додаткові активності {activities.length > 0 ? `(${activities.length})` : "(необов'язково)"}
                  </button>
                ) : (
                  <div className="mt-1.5">
                    <div className="flex flex-wrap gap-1.5">
                      {ADDITIONAL_ACTIVITIES.map((a) => {
                        const on = activities.includes(a);
                        return (
                          <button
                            key={a}
                            type="button"
                            aria-pressed={on}
                            onClick={() => toggleActivity(a)}
                            className={cn(
                              "rounded-lg border px-2.5 py-1 text-xs transition-colors",
                              on ? "border-primary bg-primary/10 font-medium text-primary" : "border-border text-muted-foreground hover:bg-accent",
                            )}
                          >
                            {ADDITIONAL_ACTIVITY_LABEL[a]}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground/80">
                      Додаткові напрямки — довідково. Гілку перевірки задає лише основна модель.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ─────────── SECTION 2: LEGAL IDENTITY (LE-grain, read-only) ─────────── */}
          <section className="rounded-xl border bg-muted/30 p-3.5">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Юридична ідентичність</span>
              <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                рівень юрособи · з 1С · read-only тут
              </span>
            </div>

            {legalMode === "b2b" && (
              <div className="space-y-1.5">
                <div className="rounded-lg border bg-background px-3 py-2.5 text-xs leading-relaxed text-foreground/90">
                  <span className="inline-flex items-center gap-1 font-medium text-muted-foreground">
                    <Lock className="h-3 w-3" /> {header.activeMembers} {pluralLe(header.activeMembers)}
                  </span>{" "}
                  — ідентичність (форма · ЄДРПОУ/ІПН) на рівні кожної юрособи, з 1С.
                  {header.legalIdentityType && (
                    <span className="mt-1 block text-muted-foreground">
                      Представник: {LEGAL_IDENTITY_LABEL[header.legalIdentityType] ?? "—"}
                      {header.identifier ? ` · ${header.identifier.label}: ${header.identifier.value}` : ""}
                    </span>
                  )}
                  <button type="button" onClick={goEntities} className="mt-1.5 block font-semibold text-primary hover:underline">
                    Редагувати у вкладці «Юрособи» →
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground/80">
                  Правова форма / ЄДРПОУ тут не задаються — холдинг не має єдиної форми.
                </p>
              </div>
            )}

            {legalMode === "breeder" && (
              <div className="space-y-1.5">
                <div className="rounded-lg border bg-background px-3 py-2.5 text-xs leading-relaxed text-foreground/90">
                  <span className="inline-flex items-center gap-1 font-medium text-muted-foreground">
                    <Lock className="h-3 w-3" /> Заводчик = ФОП / фізособа
                  </span>{" "}
                  — «Організація» недоступна. Ідентифікація (ІПН) ведеться окремо.
                  <button type="button" onClick={goEntities} className="mt-1.5 block font-semibold text-primary hover:underline">
                    Відкрити «Ідентифікація / ФОП» →
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground/80">
                  Тип ідентичності для заводчика форсовано. Редагування — у simplified identity, не в класифікації.
                </p>
              </div>
            )}

            {legalMode === "unknown" && (
              <div className="rounded-lg border bg-background px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
                Спочатку оберіть <b className="font-semibold text-foreground">тип запису</b>. Юридична
                ідентичність ведеться на рівні юрособи, не тут.
              </div>
            )}

            {legalMode === "none" && (
              <div className="rounded-lg border bg-background px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
                Юридична ідентичність <b className="font-semibold text-foreground">не потрібна</b> для цього типу запису.
              </div>
            )}
          </section>

          {mutation.isError && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {(mutation.error as Error)?.message ?? "Не вдалося зберегти класифікацію."}
            </p>
          )}

          <p className="text-[11px] leading-relaxed text-muted-foreground/80">
            Зберігається лише <b className="font-semibold text-foreground">тип запису + бізнес-модель + додаткові активності</b> (рівень
            клієнта). Юридична ідентичність тут read-only — редагується на юрособі.
          </p>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
            Скасувати
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={submit}
            title={recordType === "unknown" ? "Оберіть тип запису" : undefined}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90",
              !canSubmit && "cursor-not-allowed opacity-50",
            )}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Зберегти класифікацію
          </button>
        </div>
      </div>
    </div>
  );
}

function pluralLe(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "юрособа";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "юрособи";
  return "юросіб";
}
